import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useRef } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import axiosInstance from "API/axiosInstance";
import { API_ENDPOINTS, getUploadUrl } from "config/api";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const AdminEventReportDialog = ({ open, onClose, showAllEvents = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventMembers, setEventMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [viewAllMembers, setViewAllMembers] = useState(showAllEvents);
  const gridRef = useRef(null);

  const getMemberColumnDefs = (showEventName = false) => [
    {
      headerName: "Photo",
      field: "img",
      width: 90,
      cellRenderer: (params) => {
        const img = params.data?.img || params.data?.Img || "";
        const fullName = params.data?.fullName || params.data?.FullName || "";
        return (
          <Avatar
            src={img ? getUploadUrl(img) : undefined}
            alt={fullName}
            sx={{ width: 32, height: 32, fontSize: 13, margin: "4px auto" }}
          >
            {fullName?.charAt(0)?.toUpperCase() || "?"}
          </Avatar>
        );
      },
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressSizeToFit: true,
    },
    ...(showEventName ? [{
      headerName: "Event",
      field: "eventName",
      valueGetter: (p) => p.data?.eventName || p.data?.EventName || "N/A",
      minWidth: 150,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    }] : []),
    {
      headerName: "Name",
      field: "fullName",
      valueGetter: (p) => p.data?.fullName || p.data?.FullName || "",
      minWidth: 140,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Batch",
      field: "customBatch",
      valueGetter: (p) => p.data?.customBatch || p.data?.CustomBatch || p.data?.batch || "N/A",
      minWidth: 110,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Role",
      field: "role",
      valueGetter: (p) => p.data?.role || p.data?.Role || p.data?.roleName || p.data?.RoleName || "N/A",
      minWidth: 120,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
    {
      headerName: "Coordinator",
      field: "isCoordinator",
      valueGetter: (p) => {
        const value = p.data?.isCoordinator !== undefined
          ? p.data.isCoordinator
          : p.data?.IsCoordinator !== undefined
          ? p.data.IsCoordinator
          : false;
        return value ? "Yes" : "No";
      },
      minWidth: 120,
      cellRenderer: (p) => (p.value || "No"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        defaultOption: "equals",
      },
    },
    {
      headerName: "Status",
      field: "isActive",
      valueGetter: (p) => {
        const value = p.data?.isActive !== undefined
          ? p.data.isActive
          : p.data?.IsActive !== undefined
          ? p.data.IsActive
          : true;
        return value ? "Active" : "Inactive";
      },
      minWidth: 110,
      cellRenderer: (p) => {
        const isActive = p.value === "Active";
        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            {isActive ? (
              <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
            ) : (
              <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />
            )}
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {p.value || "Inactive"}
            </Typography>
          </Box>
        );
      },
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        defaultOption: "equals",
      },
    },
    {
      headerName: "Joined Date",
      field: "joinedDate",
      valueGetter: (p) => {
        const date = p.data?.joinedDate || p.data?.JoinedDate || "";
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
      minWidth: 130,
      cellRenderer: (p) => (p.value || "N/A"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains", "equals", "startsWith", "endsWith"],
        defaultOption: "contains",
      },
    },
  ];

  useEffect(() => {
    if (open) {
      setViewAllMembers(showAllEvents);
      fetchEvents();
    }
  }, [open, showAllEvents]);

  useEffect(() => {
    if (open && viewAllMembers && events.length > 0) {
      fetchAllEventMembers();
    }
  }, [events.length, viewAllMembers, open]);

  useEffect(() => {
    if (selectedEvent && !viewAllMembers) {
      fetchEventMembers(selectedEvent.eventId);
    }
  }, [selectedEvent, viewAllMembers]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTER_EVENTS_ADMIN);
      if (response.data) {
        setEvents(response.data);
        if (response.data.length > 0 && !selectedEvent) {
          setSelectedEvent(response.data[0]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.Message || "Failed to fetch events");
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventMembers = async (eventId) => {
    try {
      setLoadingMembers(true);
      const response = await axiosInstance.post(
        API_ENDPOINTS.PROFILE.FETCH_EVENTS_MEMBERS,
        { eventId }
      );
      if (response.data) {
        setEventMembers(response.data);
      }
    } catch (err) {
      console.error("Error fetching event members:", err);
      setEventMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAllEventMembers = async () => {
    try {
      setLoadingMembers(true);
      // First ensure events are loaded
      let eventsList = events;
      if (eventsList.length === 0) {
        const eventsResponse = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTER_EVENTS_ADMIN);
        if (eventsResponse.data) {
          eventsList = eventsResponse.data;
        }
      }
      
      // Fetch all event members from all events
      const allMembers = [];
      for (const event of eventsList) {
        try {
          const response = await axiosInstance.post(
            API_ENDPOINTS.PROFILE.FETCH_EVENTS_MEMBERS,
            { eventId: event.eventId }
          );
          if (response.data && Array.isArray(response.data)) {
            allMembers.push(...response.data);
          }
        } catch (err) {
          console.error(`Error fetching members for event ${event.eventId}:`, err);
        }
      }
      setEventMembers(allMembers);
    } catch (err) {
      console.error("Error fetching all event members:", err);
      setEventMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleExportCSV = () => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: viewAllMembers ? "all_event_members.csv" : `event_${selectedEvent?.eventName || "members"}_members.csv`,
      });
    }
  };

  const handleEventSelect = (event) => {
    if (event === "all") {
      setViewAllMembers(true);
      setSelectedEvent(null);
      fetchAllEventMembers();
    } else {
      setViewAllMembers(false);
      setSelectedEvent(event);
      setActiveTab(0);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    setSelectedEvent(null);
    setEventMembers([]);
    setActiveTab(0);
    setViewAllMembers(false);
    onClose();
  };

  const getEventStatus = (eventDate) => {
    if (!eventDate) return "Unknown";
    const date = new Date(eventDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date < today) return "Completed";
    if (date.getTime() === today.getTime()) return "Ongoing";
    if (date.getTime() === today.getTime() + 86400000) return "Tomorrow";
    return "Upcoming";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "default";
      case "Ongoing":
        return "warning";
      case "Tomorrow":
        return "info";
      case "Upcoming":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon color="primary" />
            <Typography variant="h5" fontWeight="bold">
              Event Reports
            </Typography>
          </Box>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Left Sidebar - Event List */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" gutterBottom fontWeight="bold" sx={{ fontSize: "0.75rem" }}>
                    All Events ({events.length})
                  </Typography>
                  <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                    <Card
                      sx={{
                        mb: 1,
                        cursor: "pointer",
                        border: viewAllMembers ? "2px solid" : "1px solid",
                        borderColor: viewAllMembers ? "primary.main" : "divider",
                        backgroundColor: viewAllMembers ? "primary.lighter" : "inherit",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                      onClick={() => handleEventSelect("all")}
                    >
                      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: "0.7rem" }}>
                          All Events Members
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                          View all members from all events
                        </Typography>
                      </CardContent>
                    </Card>
                    {events.map((event) => {
                      const status = getEventStatus(event.eventDate);
                      return (
                        <Card
                          key={event.eventId}
                          sx={{
                            mb: 1,
                            cursor: "pointer",
                            border:
                              selectedEvent?.eventId === event.eventId
                                ? "2px solid"
                                : "1px solid",
                            borderColor:
                              selectedEvent?.eventId === event.eventId
                                ? "primary.main"
                                : "divider",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                          onClick={() => handleEventSelect(event)}
                        >
                          <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
                              {event.eventName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem" }}>
                              {event.chapterName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem" }}>
                              {event.eventDate
                                ? new Date(event.eventDate).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                            <Box mt={0.5}>
                              <Chip
                                label={status}
                                size="small"
                                color={getStatusColor(status)}
                                sx={{ height: 18, fontSize: "0.65rem" }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Content - Event Details */}
            <Grid item xs={12} md={9}>
              {viewAllMembers ? (
                <Card>
                  <CardContent>
                    <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          All Events Members
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Members: {eventMembers.length}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DescriptionIcon />}
                        onClick={handleExportCSV}
                        disabled={eventMembers.length === 0}
                      >
                        Export CSV
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {loadingMembers ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    ) : eventMembers.length === 0 ? (
                      <Alert severity="info">No members found.</Alert>
                    ) : (
                      <Box className="ag-theme-alpine" sx={{ width: "100%", height: 500, "& .ag-cell": { fontSize: 12 } }}>
                        <AgGridReact
                          ref={gridRef}
                          rowData={eventMembers}
                          columnDefs={getMemberColumnDefs(true)}
                          domLayout="normal"
                          animateRows
                          suppressCellFocus
                          suppressRowClickSelection
                          headerHeight={48}
                          rowHeight={42}
                          pagination
                          paginationPageSize={20}
                          menuTabs={["filterMenuTab", "generalMenuTab"]}
                          floatingFilter={true}
                          defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true,
                            wrapHeaderText: true,
                            autoHeaderHeight: true,
                            menuTabs: ["filterMenuTab", "generalMenuTab"],
                            floatingFilter: true,
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : selectedEvent ? (
                <Card>
                  <CardContent>
                    <Box mb={2}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {selectedEvent.eventName}
                      </Typography>
                      <Box display="flex" gap={1} mb={1}>
                        <Chip
                          label={getEventStatus(selectedEvent.eventDate)}
                          color={getStatusColor(getEventStatus(selectedEvent.eventDate))}
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                      <Tab label="Event Details" />
                      <Tab label="Members" />
                    </Tabs>

                    <Divider sx={{ mb: 2 }} />

                    {/* Tab Panel 0: Event Details */}
                    {activeTab === 0 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <CalendarTodayIcon color="primary" fontSize="small" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                Event Date
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {selectedEvent.eventDate
                                ? new Date(selectedEvent.eventDate).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })
                                : "Not specified"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <LocationOnIcon color="primary" fontSize="small" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                Venue
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {selectedEvent.venue || "Not specified"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <GroupWorkIcon color="primary" fontSize="small" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                Chapter
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {selectedEvent.chapterName || "Not specified"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Total Members
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {eventMembers.length}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box mb={2}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <DescriptionIcon color="primary" fontSize="small" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                Description
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {selectedEvent.description || "No description available"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Created At
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedEvent.createdAt
                                ? new Date(selectedEvent.createdAt).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    {/* Tab Panel 1: Members */}
                    {activeTab === 1 && (
                      <Box>
                        <Box mb={2} display="flex" justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<DescriptionIcon />}
                            onClick={handleExportCSV}
                            disabled={eventMembers.length === 0}
                          >
                            Export CSV
                          </Button>
                        </Box>
                        {loadingMembers ? (
                          <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                          </Box>
                        ) : eventMembers.length === 0 ? (
                          <Alert severity="info">No members found for this event.</Alert>
                        ) : (
                          <Box className="ag-theme-alpine" sx={{ width: "100%", height: 400, "& .ag-cell": { fontSize: 12 } }}>
                            <AgGridReact
                              ref={gridRef}
                              rowData={eventMembers}
                              columnDefs={getMemberColumnDefs(false)}
                              domLayout="autoHeight"
                              animateRows
                              suppressCellFocus
                              suppressRowClickSelection
                              headerHeight={48}
                              rowHeight={42}
                              pagination
                              paginationPageSize={10}
                              menuTabs={["filterMenuTab", "generalMenuTab"]}
                              defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                                wrapHeaderText: true,
                                autoHeaderHeight: true,
                                menuTabs: ["filterMenuTab", "generalMenuTab"],
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="info">Please select an event or &quot;All Events Members&quot; to view details.</Alert>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}  color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AdminEventReportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showAllEvents: PropTypes.bool,
};

export default AdminEventReportDialog;
